import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import { LinkOutlined, PlayCircleFilled } from '@ant-design/icons';

import { getVideoEmbed } from './workflowMarketplace.helpers';

function VideoTile({ resource, onPlay }) {
  const video = getVideoEmbed(resource.url);
  const thumbnail = resource.thumbnailUrl || video.thumbnailUrl;
  const canEmbed = !!video.embedUrl;
  const canOpenExternally = !!video.externalUrl;

  const onOpen = () => {
    if (canEmbed) {
      onPlay(video);
      return;
    }
    if (canOpenExternally) {
      window.open(video.externalUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="workflow-resource-video">
      <button
        type="button"
        className="workflow-resource-video-tile"
        onClick={onOpen}
        disabled={!canEmbed && !canOpenExternally}
        aria-label={canEmbed ? `Play ${resource.title}` : `Open ${resource.title}`}
      >
        {thumbnail ? (
          <img src={thumbnail} alt="" className="workflow-resource-video-thumb" />
        ) : (
          <div className="workflow-resource-video-placeholder" />
        )}
        <PlayCircleFilled className="workflow-resource-video-play" />
      </button>
      <div className="workflow-resource-video-title">{resource.title}</div>
    </div>
  );
}

VideoTile.propTypes = {
  resource: PropTypes.shape({
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    thumbnailUrl: PropTypes.string,
  }).isRequired,
  onPlay: PropTypes.func.isRequired,
};

export default function WorkflowResources({ resources }) {
  const [playing, setPlaying] = useState(null);
  const visible = useMemo(
    () => (resources || []).filter((resource) => resource?.title && resource?.url),
    [resources],
  );

  if (!visible.length) return null;

  const videos = visible.filter((resource) => resource.type === 'video');
  const links = visible.filter((resource) => resource.type !== 'video');

  return (
    <div className="workflow-details-resources">
      <div className="workflow-details-sub-title" style={{ marginTop: 12 }}>Resources</div>
      {videos.length > 0 && (
        <div className="workflow-resource-videos">
          {videos.map((resource) => (
            <VideoTile
              key={resource.id || resource.url}
              resource={resource}
              onPlay={setPlaying}
            />
          ))}
        </div>
      )}
      {links.map((resource) => (
        <a
          key={resource.id || resource.url}
          className="workflow-resource-link"
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <LinkOutlined />
          <span>{resource.title}</span>
        </a>
      ))}
      <Modal
        visible={!!playing}
        footer={null}
        onCancel={() => setPlaying(null)}
        width="90vw"
        destroyOnClose
        centered
        wrapClassName="workflow-resource-video-modal-wrap"
      >
        {playing?.embedUrl && (
          <div className="workflow-resource-video-fullscreen">
            <iframe
              title="Workflow resource video"
              src={playing.embedUrl}
              allow="autoplay; fullscreen"
              allowFullScreen
              sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox"
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

WorkflowResources.propTypes = {
  resources: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
    url: PropTypes.string,
    thumbnailUrl: PropTypes.string,
  })),
};

WorkflowResources.defaultProps = {
  resources: [],
};
